import { store } from '../store';
import { setWsConnected, syncOrderUpdate } from '../store/slices/orderSlice';
import { setPaymentError } from '../store/slices/uiSlice';
import type { PaymentErrorNotification } from '../types/payment';

class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout = 1000; // Start with 1 second
  private currentOrderId: string | null = null;

  connect(orderId: string) {
    if (this.ws?.readyState === WebSocket.OPEN && this.currentOrderId === orderId) {
      console.log('WebSocket already connected for order:', orderId);
      return;
    }

    // Disconnect existing connection if any
    this.disconnect();

    this.currentOrderId = orderId;

    // Get base URL from environment variable or fallback to localhost
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    // Ensure we're using the correct protocol (ws/wss) based on the base URL
    const wsProtocol = baseUrl.startsWith('https') ? 'wss' : 'ws';
    // Remove trailing /api if it exists to avoid duplication
    const cleanBaseUrl = baseUrl.replace(/\/api\/?$/, '');
    const wsUrl = cleanBaseUrl.replace(/^https?:\/\//, '') + `/api/ws/orders/${orderId}`;
    const fullWsUrl = `${wsProtocol}://${wsUrl}`;
    
    console.log('Environment:', import.meta.env.MODE);
    console.log('Base URL:', baseUrl);
    console.log('Cleaned Base URL:', cleanBaseUrl);
    console.log('WebSocket Protocol:', wsProtocol);
    console.log('WebSocket URL:', fullWsUrl);
    console.log('WebSocket readyState:', this.ws?.readyState);

    try {
      this.ws = new WebSocket(fullWsUrl);
      console.log('WebSocket instance created');

      this.ws.onopen = () => {
        console.log('WebSocket connected successfully');
        this.reconnectAttempts = 0;
        this.reconnectTimeout = 1000;
        store.dispatch(setWsConnected(true));
      };

      this.ws.onmessage = (event) => {
        console.log('WebSocket message received:', event.data);
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'ORDER_UPDATED') {
            console.log('Dispatching order update:', data.order);
            store.dispatch(syncOrderUpdate(data.order));
          } else if (data.type === 'PAYMENT_ERROR') {
            console.log('Dispatching payment error:', data);
            store.dispatch(setPaymentError(data as PaymentErrorNotification));
          } else if (data.type === 'SESSION_INVALIDATED') {
            console.log('Session invalidated via WebSocket:', data);
            // Redirect to session expired page
            window.location.href = '/session-expired';
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.ws.onclose = (event) => {
        console.log('WebSocket closed:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean
        });
        store.dispatch(setWsConnected(false));
        this.attemptReconnect(orderId);
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
    }
  }

  private attemptReconnect(orderId: string) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      this.reconnectTimeout *= 2; // Exponential backoff
      console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${this.reconnectTimeout}ms`);
      setTimeout(() => {
        this.connect(orderId);
      }, this.reconnectTimeout);
    } else {
      console.log('Max reconnection attempts reached');
    }
  }

  disconnect() {
    if (this.ws) {
      console.log('Disconnecting WebSocket');
      this.ws.close();
      this.ws = null;
      this.currentOrderId = null;
    }
  }
}

export const wsService = new WebSocketService(); 